import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { STATUS_COLOR, PRIORITY_COLOR, fmtDate, fmtDateTime, fmtCurrency } from "@/lib/utils-domain";
import { ArrowLeft, Briefcase, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CaseDetail() {
    const { caseId } = useParams();
    const [c, setC] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [docs, setDocs] = useState([]);
    const [entries, setEntries] = useState([]);
    const [tab, setTab] = useState("overview");

    const load = async () => {
        const [r, t, d, e] = await Promise.all([
            api.get(`/cases/${caseId}`),
            api.get(`/tasks?case_id=${caseId}`),
            api.get(`/documents?case_id=${caseId}`),
            api.get(`/time-entries?case_id=${caseId}`),
        ]);
        setC(r.data); setTasks(t.data); setDocs(d.data); setEntries(e.data);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [caseId]);

    const changeStatus = async (status) => {
        await api.patch(`/cases/${caseId}`, { status });
        toast.success(`Status → ${status}`);
        load();
    };

    if (!c) return <div className="text-sm font-mono text-slate-500">Loading…</div>;

    return (
        <div className="max-w-7xl mx-auto animate-fade-up">
            <Link to="/app/cases" className="text-xs text-slate-500 hover:text-[#0B2447] inline-flex items-center gap-1 mb-4">
                <ArrowLeft size={12} /> Back to cases
            </Link>

            <div className="card-sharp p-7 mb-6">
                <div className="flex flex-col lg:flex-row gap-6 lg:items-start justify-between">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            <Briefcase size={14} weight="duotone" className="text-[#0B2447]" />
                            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{c.case_type}</span>
                        </div>
                        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{c.name}</h1>
                        <p className="text-sm text-slate-600 mt-2 max-w-3xl">{c.description || "No description."}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 items-start">
                        {["Intake", "Discovery", "Negotiation", "Trial", "Closed"].map((s) => (
                            <button
                                key={s}
                                onClick={() => changeStatus(s)}
                                data-testid={`status-${s.toLowerCase()}`}
                                className={`badge ${c.status === s ? STATUS_COLOR[s] + " ring-2 ring-offset-1 ring-[#0B2447]" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"} cursor-pointer`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-7">
                    <Stat label="Client" value={c.client?.name || "—"} />
                    <Stat label="Documents" value={c.documents_count} />
                    <Stat label="Tasks" value={c.tasks_count} />
                    <Stat label="Billable" value={fmtCurrency(c.billable_amount)} sub={`${(c.billable_minutes / 60).toFixed(1)} hrs`} />
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200 mb-6 flex gap-1">
                {[
                    ["overview", "Overview"],
                    ["timeline", "Timeline"],
                    ["documents", `Documents (${docs.length})`],
                    ["tasks", `Tasks (${tasks.length})`],
                    ["billing", `Billing (${entries.length})`],
                ].map(([k, label]) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        data-testid={`case-tab-${k}`}
                        className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition ${
                            tab === k ? "border-[#0B2447] text-[#0B2447]" : "border-transparent text-slate-500 hover:text-slate-800"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {tab === "overview" && (
                <div className="grid lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 card-sharp p-6">
                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Client</div>
                        <h3 className="font-display text-xl font-bold mt-1">{c.client?.name}</h3>
                        <dl className="mt-4 grid grid-cols-2 gap-y-3 text-sm">
                            <dt className="text-slate-500">Email</dt><dd className="text-slate-900">{c.client?.email || "—"}</dd>
                            <dt className="text-slate-500">Phone</dt><dd className="text-slate-900 font-mono">{c.client?.phone || "—"}</dd>
                        </dl>
                    </div>
                    <div className="card-sharp p-6">
                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Opened</div>
                        <div className="font-display text-xl font-bold mt-1">{fmtDate(c.opened_at)}</div>
                    </div>
                </div>
            )}

            {tab === "timeline" && (
                <div className="card-sharp p-6">
                    <h3 className="font-display text-xl font-bold mb-5">Activity timeline</h3>
                    <ol className="relative border-l-2 border-slate-200 space-y-5 pl-5">
                        {c.activities.length === 0 && <li className="text-slate-500 text-sm">No activity yet.</li>}
                        {c.activities.map((a) => (
                            <li key={a.activity_id} className="relative">
                                <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-[#0B2447] border-2 border-white" />
                                <div className="flex items-center gap-2 mb-1">
                                    <ChatCircleDots size={14} weight="duotone" className="text-slate-400" />
                                    <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{fmtDateTime(a.timestamp)}</span>
                                </div>
                                <div className="text-sm text-slate-800">{a.detail}</div>
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {tab === "documents" && (
                <div className="card-sharp">
                    <table className="w-full text-sm">
                        <thead className="border-b border-slate-200 bg-slate-50">
                            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                                <th className="px-5 py-3">Name</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Added</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {docs.map((d) => (
                                <tr key={d.document_id} className="hover:bg-slate-50/60">
                                    <td className="px-5 py-3 font-medium"><Link to={`/app/documents?open=${d.document_id}`}>{d.name}</Link></td>
                                    <td className="px-5 py-3 text-slate-500">{d.doc_type}</td>
                                    <td className="px-5 py-3 text-slate-500 font-mono text-xs">{fmtDate(d.created_at)}</td>
                                </tr>
                            ))}
                            {docs.length === 0 && <tr><td className="px-5 py-8 text-center text-slate-500" colSpan={3}>No documents.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "tasks" && (
                <div className="space-y-2">
                    {tasks.map((t) => (
                        <div key={t.task_id} className="card-sharp p-4 flex items-center justify-between">
                            <div>
                                <div className="font-semibold">{t.title}</div>
                                <div className="text-xs text-slate-500 mt-0.5">Due {fmtDate(t.due_date)} · {t.status}</div>
                            </div>
                            <span className={`badge ${PRIORITY_COLOR[t.priority]}`}>{t.priority}</span>
                        </div>
                    ))}
                    {tasks.length === 0 && <div className="card-sharp p-8 text-center text-sm text-slate-500">No tasks on this case yet.</div>}
                </div>
            )}

            {tab === "billing" && (
                <div className="card-sharp">
                    <table className="w-full text-sm">
                        <thead className="border-b border-slate-200 bg-slate-50">
                            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                                <th className="px-5 py-3">Description</th><th className="px-5 py-3">Started</th>
                                <th className="px-5 py-3 text-right">Minutes</th><th className="px-5 py-3 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {entries.map((e) => (
                                <tr key={e.entry_id}>
                                    <td className="px-5 py-3">{e.description || "—"}</td>
                                    <td className="px-5 py-3 text-slate-500 font-mono text-xs">{fmtDateTime(e.started_at)}</td>
                                    <td className="px-5 py-3 text-right font-mono">{e.minutes}</td>
                                    <td className="px-5 py-3 text-right font-mono font-semibold">{fmtCurrency(e.amount)}</td>
                                </tr>
                            ))}
                            {entries.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500">No time logged.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, sub }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{label}</div>
            <div className="font-display text-xl font-bold mt-1 text-slate-900">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}
